import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, renderList as _renderList, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d3c139a8"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["data-testid"]
const _hoisted_2 = {
  key: 0,
  class: "navigation-tab-item_icon-wrapper"
}
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = { class: "navigation-tab-item_title-wrapper_notifications-container" }
const _hoisted_5 = { class: "navigation-tab-children_list flex" }

import {NavigationTabsId} from '@//common/api';
    import {useDelayedMount, useDisplayMobile, useHover} from '@/logged/common/composables';
    import {Skeleton, SvgIcon} from '@/logged/common/ui';
    import {useAppContext} from '@/logged/config';
    import {NotificationBadge} from '@/logged/features/notifications';
    import {computed, nextTick, onMounted, ref, toRefs, watch, AnchorHTMLAttributes} from 'vue';
    import {storeToRefs} from 'pinia';
    import {bool, object} from 'vue-types';
    import {useAsideMenu} from '../aside-menu.store';
    import {NavigationTab} from '../aside-menu.types';
    import NavigationTabItemChild from './NavigationTabItemChild.vue';
    import {useDisplayMode, useExpandedTabs} from '../stores';

    
export default /*#__PURE__*/_defineComponent({
  __name: 'NavigationTabItem',
  props: {
        /** Main tab object */
        tab: object<NavigationTab<string | NavigationTabsId>>().isRequired,
        /** Hide children on Full mode for specific tabs (ex: Language selection) */
        hideCollapseOnFull: bool(),
        selector: bool(),
    },
  setup(__props, { expose: __expose }) {

const props = __props;

    

    const {tab} = toRefs(props);
    const appContext = useAppContext();
    const {isDisplayMobile} = useDisplayMobile();
    const asideMenuStore = useAsideMenu();
    const expandedTabsStore = useExpandedTabs();
    const displayModeStore = useDisplayMode();
    const {isDisplayModeFull, isDisplayModeReduced} = storeToRefs(displayModeStore);

    const tabChildrenRef = ref<HTMLDivElement>();

    const {isHovering} = useHover();

    /** Dynamic component type */
    const getComponentType = computed<'div' | 'a'>(() => (tab.value.url ? 'a' : 'div'));

    // - Notifications

    const getNotificationsCount = computed<number>(() => {
        const baseCount = props.tab.notificationsCount ?? 0;
        if (props.tab.children?.length) {
            return props.tab.children.reduce((acc, value) => (acc += value.notificationsCount ?? 0), 0) + baseCount;
        } else {
            return baseCount;
        }
    });

    const isNotificationBadgeVisibleForReduced = computed(
        () => isDisplayModeReduced.value && getNotificationsCount.value > 0 && (isCollapsable.value ? !isHoveringOnReduced.value : true),
    );

    // - Hovering reduced mode
    const isHoveringOnReduced = ref(false);
    const isAnchoredBottom = ref(false);
    let timeOutHovering: number | null = null;

    const isCollapseMenuDisplaying = computed(
        () =>
            (isCollapsable.value && (isDisplayModeFull.value || isDisplayMobile.value) && !props.hideCollapseOnFull) ||
            ((isDisplayModeReduced.value || props.tab.showChildrenInADropdown) && isHoveringOnReduced.value),
    );

    function handleMouseEnter() {
        if (isDisplayModeReduced.value || props.tab.showChildrenInADropdown) {
            showChildrenInReduced();
        }
    }
    function handleMouseLeave() {
        if (isDisplayModeReduced.value || props.tab.showChildrenInADropdown) {
            if (timeOutHovering) {
                window.clearTimeout(timeOutHovering);
            }
            isHoveringOnReduced.value = false;
            isAnchoredBottom.value = false;
        }
    }

    function showChildrenInReduced() {
        if (timeOutHovering) {
            window.clearTimeout(timeOutHovering);
        }
        timeOutHovering = window.setTimeout(() => {
            isHoveringOnReduced.value = true;
            nextTick().then(() => {
                if (tabChildrenRef.value) {
                    const {height, top} = tabChildrenRef.value.getBoundingClientRect();
                    const offsetTop = height + top;
                    const threshold = 20;
                    if (offsetTop >= document.documentElement.clientHeight - threshold) {
                        isAnchoredBottom.value = true;
                    } else {
                        isAnchoredBottom.value = false;
                    }
                }
            });
        }, 200);
    }

    // - Collapse and expand tabs

    const {isMounted: isMountedDelayed} = useDelayedMount(100);

    const isCollapsable = computed<boolean>(() => !!tab.value.children?.length && !props.tab.showChildrenInADropdown);
    const isExpanded = computed<boolean>(
        () => expandedTabsStore.expandedTabs.includes(tab.value.id) && (isDisplayModeFull.value || isDisplayMobile.value),
    );

    let expandTimeOut: number | null = null;

    async function onExpandChange(fromDisplayMode = false) {
        if (isDisplayModeFull.value || isDisplayMobile.value) {
            await nextTick();
            const panel = tabChildrenRef.value;
            const transitionDuration = 200;
            if (panel) {
                if (expandTimeOut) {
                    window.clearTimeout(expandTimeOut);
                }
                /** Avoid fixed size after transition */
                if (!isExpanded.value) {
                    // Panel is closing
                    if (isMountedDelayed.value) {
                        panel.style.maxHeight = `${panel.scrollHeight}px`;
                        // Prevent animation on first mount
                        panel.style.transition = 'max-height 0.2s ease-out';
                    }
                    window.setTimeout(() => {
                        panel.style.maxHeight = '0';
                    }, 1);
                } else {
                    // Panel is opening
                    if (isMountedDelayed.value) {
                        // Prevent animation on first mount
                        panel.style.transition = 'max-height 0.2s ease-out';
                    }
                    panel.style.maxHeight = `${panel.scrollHeight}px`;
                    expandTimeOut = window.setTimeout(() => {
                        panel.style.maxHeight = 'none';
                        panel.style.transition = '';
                    }, transitionDuration);
                }
            }
        } else if (fromDisplayMode) {
            const panel = tabChildrenRef.value;
            if (panel) {
                // Panel is closing
                panel.style.maxHeight = `${panel.scrollHeight}px`;
                // Prevent animation on first mount
                panel.style.transition = 'max-height 0.2s ease-out';
                window.setTimeout(() => {
                    panel.style.maxHeight = '0';
                }, 1);
            }
        }
    }

    function handleClick() {
        if ((isCollapsable.value && isDisplayModeFull.value) || isDisplayMobile.value) {
            expandedTabsStore.toggleExpandTab(tab.value.id);
        } else if (tab.value.action && typeof tab.value.action === 'function') {
            tab.value.action();
        }
    }

    watch(isExpanded, onExpandChange);
    watch(isDisplayModeReduced, (value) => onExpandChange(value));
    onMounted(onExpandChange);

    // - Active state

    const isActive = computed<boolean>(() => asideMenuStore.currentActiveRoute === tab.value.id || isAnyChildActive.value);
    const isAnyChildActive = computed<boolean>(() => !!tab.value.children?.some((child) => isTabActive(child.id)));

    function isTabActive(id: string) {
        return asideMenuStore.currentActiveRoute === id;
    }

    // - Attributes
    const getComputedAttributes = computed<Partial<AnchorHTMLAttributes>>(() => {
        if (!isCollapsable.value && tab.value.url) {
            return {
                href: tab.value.url.href,
                ...(tab.value.url.blank && {
                    target: '_blank',
                    rel: 'noopener noreferer',
                }),
                ...(tab.value.url.heapId && {'data-heap': tab.value.url.heapId}),
            };
        } else {
            return {};
        }
    });

    const getTransitionMode = computed<'fade-opacity' | 'default'>(() => {
        if (isExpanded.value) {
            return 'default';
        } else {
            return 'fade-opacity';
        }
    });

    // - Children

    const getChildren = computed(() => {
        if (isDisplayModeReduced.value && !props.tab.children?.length && !props.selector) {
            return [tab.value];
        } else {
            return props.tab.children;
        }
    });

    // Type-check in unit tests
    __expose({
        isCollapsable,
        isExpanded,
        isActive,
        isAnyChildActive,
        getComponentType,
    });

return (_ctx: any,_cache: any) => {
  const _component_joy_icon = _resolveComponent("joy-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["navigation-tab flex", {
            isActive: isActive.value,
            isCollapsable: isCollapsable.value,
            isExpanded: isExpanded.value,
            isFull: _unref(isDisplayModeFull),
            isReduced: _unref(isDisplayModeReduced),
            isChildrenShownInADropdown: _unref(tab).showChildrenInADropdown,
            isMobile: _unref(isDisplayMobile),
            isHovering: isHoveringOnReduced.value,
            isAnchoredBottom: isAnchoredBottom.value,
        }]),
    style: _normalizeStyle(_unref(tab).customStyle),
    "data-testid": _unref(tab).id,
    onMouseenter: handleMouseEnter,
    onMouseleave: handleMouseLeave
  }, [
    (_openBlock(), _createBlock(_resolveDynamicComponent(getComponentType.value), _mergeProps({
      class: ["navigation-tab-item flex", {hasBorder: _unref(tab).border}],
      "data-testid": `${_unref(tab).id}-Item`
    }, getComputedAttributes.value, {
      "aria-label": _unref(tab).title,
      onClick: handleClick
    }), {
      default: _withCtx(() => [
        (!_unref(asideMenuStore).hydrateFromCache && _unref(appContext).loading && _unref(tab).constraint != null)
          ? (_openBlock(), _createBlock(_unref(Skeleton), {
              key: 0,
              height: "20px",
              width: _unref(isDisplayModeReduced) ? '20px' : '100%'
            }, null, 8, ["width"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (_unref(tab).icons?.mainIcon)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(_unref(SvgIcon), {
                      src: _unref(tab).icons.mainIcon,
                      active: isActive.value || _unref(isHovering),
                      name: _unref(tab).title,
                      "can-be-active": _unref(tab).icons.canBeActive
                    }, null, 8, ["src", "active", "name", "can-be-active"]),
                    _createVNode(_Transition, {
                      name: "fade-opacity",
                      mode: "out-in"
                    }, {
                      default: _withCtx(() => [
                        (isNotificationBadgeVisibleForReduced.value)
                          ? (_openBlock(), _createBlock(_unref(NotificationBadge), {
                              key: 0,
                              count: getNotificationsCount.value,
                              offset: 0
                            }, null, 8, ["count"]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_Transition, {
                name: "fade-opacity",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (_unref(isDisplayModeFull) || _unref(isDisplayMobile))
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "navigation-tab-item_title-wrapper",
                        "aria-label": _unref(tab).title
                      }, [
                        _createElementVNode("div", _hoisted_4, [
                          _createElementVNode("span", null, _toDisplayString(_unref(tab).title), 1),
                          (_unref(tab).icons?.altIcon)
                            ? (_openBlock(), _createBlock(_unref(SvgIcon), {
                                key: 0,
                                class: "alt-icon",
                                src: _unref(tab).icons.altIcon,
                                name: "misc",
                                "can-be-active": false,
                                size: 14
                              }, null, 8, ["src"]))
                            : _createCommentVNode("", true),
                          _createVNode(_Transition, { name: "fade-opacity" }, {
                            default: _withCtx(() => [
                              (getNotificationsCount.value > 0 && !isExpanded.value)
                                ? (_openBlock(), _createBlock(_unref(NotificationBadge), {
                                    key: 0,
                                    count: getNotificationsCount.value,
                                    offset: 0
                                  }, null, 8, ["count"]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          })
                        ])
                      ], 8, _hoisted_3))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(_Transition, {
                name: "fade-opacity",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (isCollapsable.value && (_unref(isDisplayModeFull) || _unref(isDisplayMobile)) && !__props.hideCollapseOnFull)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass([{isExpanded: isExpanded.value}, "navigation-tab-item_collapse-icon-wrapper flex"])
                      }, [
                        _createVNode(_component_joy_icon, {
                          name: "chevron-down",
                          color: "grey"
                        })
                      ], 2))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 64))
      ]),
      _: 1
    }, 16, ["class", "data-testid", "aria-label"])),
    _createVNode(_Transition, {
      name: getTransitionMode.value,
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (isCollapseMenuDisplaying.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "tabChildrenRef",
              ref: tabChildrenRef,
              class: _normalizeClass([{
                    isExpanded: isCollapsable.value && isExpanded.value,
                    isReduced: _unref(isDisplayModeReduced),
                    isChildrenShownInADropdown: _unref(tab).showChildrenInADropdown,
                    isMobile: _unref(isDisplayMobile),
                    isAnchoredBottom: isAnchoredBottom.value,
                    isEmpty: !_unref(tab).children?.length,
                }, "navigation-tab-children flex"])
            }, [
              (_unref(isDisplayModeReduced) && _unref(tab).showTitleOnHover)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(getComponentType.value), _mergeProps({ key: 0 }, getComputedAttributes.value, {
                    class: [{isEmpty: !_unref(tab).children, isActive: isActive.value}, "navigation-tab-children_reduced-title flex"]
                  }), {
                    default: _withCtx(() => [
                      _createElementVNode("span", null, _toDisplayString(_unref(tab).title), 1)
                    ]),
                    _: 1
                  }, 16, ["class"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getChildren.value, (childTab) => {
                  return (_openBlock(), _createBlock(NavigationTabItemChild, {
                    key: childTab.id,
                    tab: childTab,
                    "is-anchored-bottom": isAnchoredBottom.value,
                    "is-shown-in-a-dropdown": _unref(tab).showChildrenInADropdown
                  }, null, 8, ["tab", "is-anchored-bottom", "is-shown-in-a-dropdown"]))
                }), 128))
              ])
            ], 2))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["name"])
  ], 46, _hoisted_1))
}
}

})